import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import API, { UPLOADS_URL } from '../api/axios';

const openMerritoPopup = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  if (typeof window.openEnquiryPopup === 'function') {
    window.openEnquiryPopup();
  } else {
    const btn = document.querySelector('.npfWidgetButton.npfWidget-ab76b73b357938fdd5c126d3bf550af2') || document.querySelector('.npfWidgetButton');
    if (btn) {
      btn.click();
    } else {
      window.location.href = '/admission#enquire';
    }
  }
};

const AGE_CRITERIA = [
  { class: 'Pre-Nursery', age: '2 years' },
  { class: 'Nursery', age: '3 years' },
  { class: 'LKG', age: '4 years' },
  { class: 'UKG', age: '5 years' },
  { class: 'Grade 1', age: '6 years' },
  { class: 'Grade 2 onwards', age: 'As per applicable age criteria' }
];

const PROCESS_STEPS = [
  { num: '01', title: 'Registration', desc: 'Complete the online registration form with accurate and complete information.', icon: 'fa-pen-to-square' },
  { num: '02', title: 'Assessment / Interaction', desc: 'The student may be required to appear for an age-appropriate assessment or interaction, as applicable.', icon: 'fa-users' },
  { num: '03', title: 'Document Submission', desc: 'Submit all required documents within the stipulated time.', icon: 'fa-file-lines' },
  { num: '04', title: 'Admission Confirmation', desc: "Admission is offered subject to eligibility, assessment, availability of seats and fulfilment of the school's admission criteria.", icon: 'fa-circle-check' },
  { num: '05', title: 'Fee Payment', desc: 'The admission and applicable school fees are required to be deposited within the prescribed timeline to confirm the admission.', icon: 'fa-credit-card' },
  { num: '06', title: 'Stay Aligned with Timelines', desc: 'Please ensure that all required steps, documents and formalities are completed within the communicated deadlines for a smooth admission process.', icon: 'fa-clock' }
];

const GUIDELINES = [
  'Registration does not guarantee admission.',
  'Admissions are subject to availability of seats and fulfilment of eligibility criteria.',
  'All information and documents submitted must be accurate and authentic.',
  'Age eligibility will be as per the applicable norms for the academic session.'
];

const CONTACT_FALLBACK = {
  phone1: '+91-9266333940',
  phone2: '+91-9266333941',
  email: 'info@sawssonipat.com',
  address: 'Block C, Ansal Sushant City, Sector 61, Kundli, Sonipat, Haryana 131028'
};

const AdmissionGuidelinesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [contact, setContact] = useState(CONTACT_FALLBACK);

  useEffect(() => {
    API.get('/pages/public/admission-guidelines')
      .then(res => {
        if (res.data?.data) setPageData(res.data.data);
      })
      .catch(() => {});

    API.get('/contacts/public')
      .then(res => {
        const c = res.data?.data;
        if (c?.phone) {
          const phones = String(c.phone).split(',').map(p => p.trim()).filter(Boolean);
          setContact({
            phone1: phones[0] ? (phones[0].startsWith('+') ? phones[0] : `+91-${phones[0]}`) : CONTACT_FALLBACK.phone1,
            phone2: phones[1] ? (phones[1].startsWith('+') ? phones[1] : `+91-${phones[1]}`) : '',
            email: c.email || CONTACT_FALLBACK.email,
            address: c.address || CONTACT_FALLBACK.address,
          });
        }
      })
      .catch(() => {});
  }, []);

  const bannerImgSrc = pageData?.banner_image
    ? `${UPLOADS_URL}/${pageData.banner_image}`
    : '/images/page-banners/admission-guidliness.jpg';

  const s = pageData?.sections || {};
  const banner = s.banner || {};
  const ageCriteria = s.age_criteria || {
    kicker: 'Eligibility',
    heading: 'Minimum age for admission.',
    note: 'The minimum age for admission is calculated as on 31st March of the year of admission, as set out below for each entry point.',
    items: AGE_CRITERIA
  };
  const process = s.process || {
    kicker: 'How it works',
    heading: 'The admission process, step by step.',
    intro: 'Six clear stages take a family from first registration to a confirmed place at the school.',
    steps: PROCESS_STEPS
  };
  const guidelines = s.guidelines || {
    kicker: 'Before you apply',
    heading: 'Important guidelines for parents.',
    intro: 'Please read these points carefully before beginning the admission process. Our admissions office is happy to answer any questions.',
    items: GUIDELINES
  };

  return (
    <>
      <SEO
        slug="admission-guidelines"
        title={banner.title ? `${banner.title} | St. Andrews World School, Sonipat` : "Admission Guidelines | St. Andrews World School, Sonipat"}
        description="Comprehensive admission guidelines, age eligibility criteria, required documents, and enrollment process for St. Andrews World School, Sonipat."
        keywords="Admission guidelines, SAWS Sonipat, admission process, age criteria, school admission"
      />

      {/* ─── HERO ─────────────────────────────────────────── */}
      <PageBanner
        title={banner.title || "A clear path to joining our community."}
        subtitle={banner.subtitle || "Admission Guidelines · Sonipat"}
        breadcrumb={banner.breadcrumb || "ADMISSION GUIDELINES"}
        bgImage={bannerImgSrc}
      />

      {/* ─── AGE CRITERIA ─────────────────────────────────────────── */}
      <section id="age-criteria" className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="mb-14 grid gap-5 md:grid-cols-2 md:items-end">
            <div>
              <span className="kicker block text-left uppercase tracking-widest text-xs font-bold text-gray-500 mb-4">
                {ageCriteria.kicker || 'Eligibility'}
              </span>
              <h2 className="mt-4 text-4xl text-primary md:text-6xl font-bold leading-tight" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                {ageCriteria.heading || 'Minimum age for admission.'}
              </h2>
            </div>
            {ageCriteria.note && (
              <p className="max-w-lg justify-self-end text-base leading-7 text-body whitespace-pre-line">
                {ageCriteria.note}
              </p>
            )}
          </div>

          <div className="grid overflow-hidden border border-gray-200 bg-white shadow-sm md:grid-cols-2">
            {(ageCriteria.items || AGE_CRITERIA).map((item, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-6 sm:px-9 ${idx % 2 !== 0 ? 'md:border-l' : ''}`}
              >
                <span className="text-xl font-semibold text-[#1b3160] md:text-2xl" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                  {item.class}
                </span>
                <span className="text-right text-base font-semibold text-[#a4862f] md:text-lg">
                  {item.age}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─────────────────────────────────────────── */}
      <section id="process" className="py-20 md:py-28 bg-[#f9f8f4]">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="mb-14 grid gap-5 md:grid-cols-2 md:items-end">
            <div>
              <span className="kicker block text-left uppercase tracking-widest text-xs font-bold text-gray-500 mb-4">
                {process.kicker || 'How it works'}
              </span>
              <h2 className="mt-4 text-4xl text-primary md:text-6xl font-bold leading-tight" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                {process.heading || 'The admission process, step by step.'}
              </h2>
            </div>
            {process.intro && (
              <p className="max-w-lg justify-self-end text-base leading-7 text-body whitespace-pre-line">
                {process.intro}
              </p>
            )}
          </div>

          <div className="grid gap-px overflow-hidden border border-gray-200 bg-gray-200 md:grid-cols-2 lg:grid-cols-3">
            {(process.steps || PROCESS_STEPS).map((step, idx) => (
              <article key={idx} className="group bg-white p-7 transition-colors hover:bg-gray-50 md:p-9">
                <div className="mb-8 flex items-center justify-between">
                  <span className="text-xs font-bold tracking-[0.2em] text-gray-400">
                    {step.num}
                  </span>
                  <i className={`fas ${step.icon || 'fa-circle-check'} text-[28px] text-[#a4862f]`} />
                </div>
                <h3 className="max-w-md text-2xl leading-tight text-primary md:text-3xl font-bold" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                  {step.title}
                </h3>
                <p className="mt-4 text-base leading-7 text-body md:text-lg">
                  {step.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BEFORE YOU APPLY ─────────────────────────────────────────── */}
      <section className="bg-[#1b3160] py-20 text-white md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="mb-14 grid gap-5 md:grid-cols-2 md:items-end">
            <div>
              <span className="kicker block text-left uppercase tracking-widest text-xs font-bold text-[#a4862f] mb-4">
                {guidelines.kicker || 'Before you apply'}
              </span>
              <h2 className="mt-4 text-4xl text-white md:text-6xl font-bold leading-tight" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                {guidelines.heading || 'Important guidelines for parents.'}
              </h2>
            </div>
            {guidelines.intro && (
              <p className="max-w-lg justify-self-end text-base leading-7 text-white/70 whitespace-pre-line">
                {guidelines.intro}
              </p>
            )}
          </div>
          <ul className="mt-12 space-y-4 list-none p-0 m-0">
            {(guidelines.items || GUIDELINES).map((guideline, idx) => (
              <li key={idx} className="flex items-start gap-4 border border-white/15 bg-white/5 p-5">
                <i className="fas fa-shield-alt mt-1 text-xl text-[#a4862f] shrink-0" />
                <span className="text-base leading-7 text-white/90">{guideline}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <button
              onClick={openMerritoPopup}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-[15px] font-bold transition-colors shadow mt-2 h-12 rounded-none bg-[#a4862f] px-7 text-[#1b3160] hover:bg-[#a4862f]/90 cursor-pointer"
            >
              Apply now <i className="fas fa-arrow-right ml-1" />
            </button>
          </div>
        </div>
      </section>

      {/* ─── GET IN TOUCH ─────────────────────────────────────────── */}
      <section className="bg-white pb-20 pt-16 md:pb-24 md:pt-20">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <span className="kicker block uppercase tracking-widest text-xs font-bold text-gray-500 mb-3">
              We're here to help
            </span>
            <h2 className="text-4xl text-primary md:text-5xl font-bold leading-tight" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
              Questions? Our admissions team will guide you.
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-4 border border-gray-200 bg-[#f9f8f4] px-6 py-5 transition-colors hover:border-[#a4862f]">
              <i className="fas fa-phone-alt text-xl text-[#a4862f] shrink-0" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Call us</p>
                <a href={`tel:${contact.phone1.replace(/[^0-9+]/g, '')}`} className="block text-sm font-semibold text-[#1b3160] hover:text-[#a4862f]">{contact.phone1}</a>
                {contact.phone2 && (
                  <a href={`tel:${contact.phone2.replace(/[^0-9+]/g, '')}`} className="block text-sm font-semibold text-[#1b3160] hover:text-[#a4862f]">{contact.phone2}</a>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 border border-gray-200 bg-[#f9f8f4] px-6 py-5 transition-colors hover:border-[#a4862f]">
              <i className="fas fa-envelope text-xl text-[#a4862f] shrink-0" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Email us</p>
                <a href={`mailto:${contact.email}`} className="block text-sm font-semibold text-[#1b3160] break-all hover:text-[#a4862f]">{contact.email}</a>
              </div>
            </div>

            <div className="flex items-center gap-4 border border-gray-200 bg-[#f9f8f4] px-6 py-5 transition-colors hover:border-[#a4862f]">
              <i className="fas fa-map-marker-alt text-xl text-[#a4862f] shrink-0" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Visit us</p>
                <p className="text-sm font-semibold text-[#1b3160] leading-relaxed">{contact.address}</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={openMerritoPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#1b3160] text-white hover:bg-[#1b3160]/90 transition-colors h-11 px-8 text-sm font-medium cursor-pointer shadow"
            >
              Apply now
              <i className="fas fa-arrow-right text-[12px]" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdmissionGuidelinesPage;
